import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMeeting } from "@/hooks/useMeeting";
import { useMeetingStore } from "@/stores/meetingStore";
import { SummaryView } from "@/components/SummaryView";
import { TranscriptView } from "@/components/TranscriptView";
import { ExportActions } from "@/components/ExportActions";
import { MeetingChat } from "@/components/MeetingChat";
import { MeetingTagsEditor } from "@/components/MeetingTagsEditor";
import { ShareLinkPanel } from "@/components/ShareLinkPanel";
import { ActionsExport } from "@/components/ActionsExport";
import { getDateLocale } from "@/lib/dateFormat";

export function MeetingDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { meeting, transcript, summary, loading, error, refresh } = useMeeting(id);
  const { retryMeeting, deleteMeeting } = useMeetingStore();
  const [tab, setTab] = useState<"summary" | "transcript">("summary");
  const [retrying, setRetrying] = useState(false);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-redi-surface rounded w-1/2" />
          <div className="h-4 bg-redi-surface rounded w-1/3" />
          <div className="h-64 bg-redi-surface rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <p className="text-redi-error">{error || t("common.error")}</p>
      </div>
    );
  }

  const date = new Date(meeting.recorded_at).toLocaleDateString(
    getDateLocale(),
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );

  const duration = meeting.duration_seconds
    ? `${Math.floor(meeting.duration_seconds / 60)} min ${meeting.duration_seconds % 60} sec`
    : null;

  const isProcessing =
    meeting.status === "transcribing" || meeting.status === "summarizing";

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryMeeting(meeting.id);
    } catch {
      // error handled by polling
    }
    setRetrying(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(t("meetingDetail.deleteMsg"))) return;
    await deleteMeeting(meeting.id);
    navigate("/meetings");
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-redi-muted hover:text-redi-text text-sm mb-4 inline-flex items-center gap-1"
        >
          &larr; {t("common.back")}
        </button>
        <h2 className="text-xl font-bold text-redi-text">
          {meeting.title || t("meetings.untitled")}
        </h2>
        <div className="flex items-center gap-3 mt-1 text-sm text-redi-muted">
          <span>{date}</span>
          {duration && <span>{duration}</span>}
        </div>
      </div>

      <MeetingTagsEditor
        meetingId={meeting.id}
        folderId={meeting.folder_id}
        tags={meeting.tags ?? []}
        onChange={() => refresh()}
      />

      {/* Context */}
      {meeting.context && (
        <div className="bg-redi-surface/50 border-l-4 border-redi-primary rounded-r-lg p-4 mb-6">
          <p className="text-sm text-redi-muted">
            <strong className="text-redi-text">{t("record.contextLabel")}</strong>{" "}
            {meeting.context}
          </p>
        </div>
      )}

      {/* Status */}
      {isProcessing && (
        <div className="bg-redi-surface rounded-xl p-6 mb-6 text-center">
          <div className="inline-block w-8 h-8 border-2 border-redi-primary border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-redi-text text-sm">
            {meeting.status === "transcribing"
              ? t("meetingDetail.statusTranscribing")
              : t("meetingDetail.statusSummarizing")}
          </p>
        </div>
      )}

      {/* Error + retry */}
      {meeting.status === "error" && (
        <div className="bg-redi-error/10 border border-redi-error/20 rounded-xl p-4 mb-6">
          <p className="text-redi-error text-sm mb-3">
            {meeting.error_message || t("common.error")}
          </p>
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="px-4 py-2 bg-redi-primary text-white text-sm rounded-lg hover:bg-redi-primary/80 disabled:opacity-50"
          >
            {retrying ? "..." : t("common.retry")}
          </button>
        </div>
      )}

      {/* Export */}
      {summary && meeting.status === "done" && (
        <div className="mb-6">
          <ExportActions
            meeting={meeting}
            summary={summary}
            transcript={transcript}
          />
        </div>
      )}

      {/* Tabs */}
      {(summary || transcript) && (
        <div className="flex gap-1 mb-4 bg-redi-surface rounded-lg p-1 w-fit">
          <button
            onClick={() => setTab("summary")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "summary"
                ? "bg-redi-primary text-white"
                : "text-redi-muted hover:text-redi-text"
            }`}
          >
            {t("summary.title")}
          </button>
          <button
            onClick={() => setTab("transcript")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "transcript"
                ? "bg-redi-primary text-white"
                : "text-redi-muted hover:text-redi-text"
            }`}
          >
            {t("transcript.title")}
          </button>
        </div>
      )}

      {/* Content */}
      {tab === "summary" && summary && (
        <>
          <SummaryView summary={summary} />
          {summary.content?.actions?.length ? (
            <ActionsExport
              meetingId={meeting.id}
              meetingTitle={meeting.title}
              actions={summary.content.actions}
            />
          ) : null}
        </>
      )}
      {tab === "transcript" && transcript && (
        <TranscriptView
          transcript={transcript}
          audioPath={meeting.audio_path}
          onEdited={() => refresh()}
        />
      )}

      {meeting.status === "done" && transcript && (
        <div className="mt-8 space-y-6">
          <MeetingChat meetingId={meeting.id} hasTranscript={true} />
          <ShareLinkPanel meetingId={meeting.id} />
        </div>
      )}

      {/* Delete */}
      <div className="mt-8 pt-4 border-t border-white/5">
        <button
          onClick={handleDelete}
          className="text-sm text-redi-error hover:text-redi-error/80"
        >
          {t("common.delete")}
        </button>
      </div>
    </div>
  );
}
